// @LTG-ID: contact-step-details
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: source `Contact.html` lines 1010-1067 (v4)
// @CHANGED: 2026-05 session_006 (CHG-0083-M-A1)
// @OWNER: web
//
// CHG-0083-M-A1 — Step 5: Details (message + optional phone).
//
// Section header includes the chosen category label.
// Textarea with category-aware placeholder (changes based on selection).
// Live character counter in the bottom-right.
// Optional phone field below.
// Validates message length ≥ 10 characters.
//
// CHG-0083-M-A1 boundary: "Submit →" calls onNext, but the parent
// MultiStepForm renders a placeholder "next-step pending" card after step 4
// instead of advancing to Sending/Confirmation (which are CHG-0083-M-A2 scope).

import { useState } from "react";
import { validateMessage, validatePhone } from "./validators";
import { CATEGORIES } from "./StepCategory";

const PLACEHOLDERS = {
    snapshot:
        "Tell us about the business and what you'd like the Snapshot to focus on. Recent revenue, what's driving the question, any specific concerns...",
    membership:
        "Tell us about your current Snapshot order, if any, and what you'd like the Membership to help you with...",
    support:
        "Tell us your order or account reference if you have one, and what's not working. We'll get back within 1 business day...",
    press:
        "Tell us about the story, the publication, the deadline, and the angle. We respond to journalist inquiries within the same business day where possible...",
    partnership:
        "Tell us about your practice or your firm, the kind of client you'd like to refer or partner on, and how you'd like to work together...",
    other: "Tell us what's on your mind. We'll route it to the right person at the firm...",
};

export const StepDetails = ({ data, setData, onNext, onBack }) => {
    const [touched, setTouched] = useState({});
    const first = (data.firstName || "").trim();
    const cat = CATEGORIES.find((c) => c.key === data.category);
    const msgCheck = validateMessage(data.message);
    const phoneCheck = validatePhone(data.phone);
    const valid = msgCheck.ok && phoneCheck.ok;

    const trySubmit = () => {
        setTouched({ message: true, phone: true });
        if (valid) onNext();
    };

    const showMsgErr = touched.message && !msgCheck.ok;
    const showPhoneErr = touched.phone && !phoneCheck.ok;

    return (
        <section
            className="ltgc-step"
            data-testid="contact-step-details"
            aria-labelledby="ltgc-step-details-heading"
        >
            <span className="ltgc-step-greeting">
                Step 5 · The details · {cat?.label}
            </span>
            <h2 id="ltgc-step-details-heading">
                {first}, <em>tell us more</em>.
            </h2>
            <p className="ltgc-sub">
                Plain English is fine. The more context, the better we can
                route it and respond. There&apos;s no character limit; write as
                much as you need.
            </p>
            <div className="ltgc-textarea-wrap">
                <textarea
                    id="ltgc-input-message"
                    className={
                        "ltgc-field-textarea " +
                        (showMsgErr ? "is-error" : "")
                    }
                    autoFocus
                    placeholder={
                        PLACEHOLDERS[data.category] || PLACEHOLDERS.other
                    }
                    value={data.message}
                    aria-required="true"
                    aria-invalid={showMsgErr ? "true" : "false"}
                    aria-describedby={
                        showMsgErr ? "ltgc-input-message-error" : undefined
                    }
                    aria-label="Tell us more"
                    data-testid="contact-input-message"
                    onChange={(e) =>
                        setData({ ...data, message: e.target.value })
                    }
                    onBlur={() =>
                        setTouched((t) => ({ ...t, message: true }))
                    }
                />
                <span
                    className="ltgc-char-count"
                    data-testid="contact-char-count"
                >
                    {data.message.length} chars
                </span>
            </div>
            {showMsgErr && (
                <span
                    id="ltgc-input-message-error"
                    className="ltgc-field-error"
                    role="alert"
                    style={{ marginTop: 4 }}
                    data-testid="contact-error-message"
                >
                    {msgCheck.msg}
                </span>
            )}
            <p className="ltgc-small-help">
                Optional. Attach a phone number if you&apos;d prefer a call
                back.
            </p>
            <input
                id="ltgc-input-phone"
                className={
                    "ltgc-field-input " + (showPhoneErr ? "is-error" : "")
                }
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="(704) 555-0100  ·  optional"
                value={data.phone || ""}
                aria-required="false"
                aria-invalid={showPhoneErr ? "true" : "false"}
                aria-describedby={
                    showPhoneErr ? "ltgc-input-phone-error" : undefined
                }
                aria-label="Phone number (optional)"
                data-testid="contact-input-phone"
                onChange={(e) =>
                    setData({ ...data, phone: e.target.value })
                }
                onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                style={{ marginTop: 6 }}
            />
            {showPhoneErr && (
                <span
                    id="ltgc-input-phone-error"
                    className="ltgc-field-error"
                    role="alert"
                    data-testid="contact-error-phone"
                >
                    {phoneCheck.msg}
                </span>
            )}
            <div className="ltgc-step-nav">
                <button
                    type="button"
                    className="ltgc-btn ltgc-btn-ghost"
                    onClick={onBack}
                    data-testid="contact-step-details-back"
                >
                    ← Back
                </button>
                <button
                    type="button"
                    className="ltgc-btn ltgc-btn-primary"
                    disabled={!valid}
                    onClick={trySubmit}
                    data-testid="contact-step-details-submit"
                >
                    Submit my message <span className="ltgc-arrow">→</span>
                </button>
            </div>
        </section>
    );
};

export default StepDetails;
